import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import journeysService from "../services/journeysService";
import { Journey } from "../types/journey";

const statusColors: Record<string, string> = {
  draft: "#6b7280",
  active: "#22c55e",
  paused: "#f59e0b",
  archived: "#9ca3af",
};

const Journeys: React.FC = () => {
  const navigate = useNavigate();
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newJourneyName, setNewJourneyName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadJourneys();
  }, []);

  const loadJourneys = async () => {
    try {
      const data = await journeysService.listJourneys();
      setJourneys(data);
    } catch (error) {
      console.error("Failed to load journeys:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newJourneyName.trim()) return;
    setCreating(true);
    try {
      const journey = await journeysService.createJourney(newJourneyName.trim());
      setShowCreateModal(false);
      setNewJourneyName("");
      navigate(`/journeys/${journey.id}`);
    } catch (error) {
      console.error("Failed to create journey:", error);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <div style={{ padding: "20px" }}>Loading journeys...</div>;
  }

  return (
    <div style={{ padding: "20px", maxWidth: "900px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Journeys</h1>
          <p style={{ margin: "4px 0 0 0", color: "#6b7280", fontSize: "14px" }}>
            Automated email sequences that send to subscribers over time based on triggers and delays.
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => navigate("/journeys/generate-from-interview")}
            style={{
              padding: "8px 16px",
              backgroundColor: "#fff",
              color: "#000",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Generate from Interview
          </button>
          <button
            className="primary-button"
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: "8px 16px",
              backgroundColor: "#000",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Create Journey
          </button>
        </div>
      </div>

      {journeys.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: "#6b7280",
          }}
        >
          <p>No journeys yet. Create your first journey to get started.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {journeys.map((journey) => (
            <div
              key={journey.id}
              onClick={() => navigate(`/journeys/${journey.id}`)}
              style={{
                padding: "16px 20px",
                backgroundColor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: "16px" }}>
                  {journey.name}
                </div>
                {journey.description && (
                  <div
                    style={{
                      color: "#6b7280",
                      fontSize: "13px",
                      marginTop: "4px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "500px",
                    }}
                  >
                    {journey.description}
                  </div>
                )}
                <div
                  style={{ color: "#6b7280", fontSize: "13px", marginTop: "4px" }}
                >
                  {journey.step_count} step{journey.step_count !== 1 ? "s" : ""}{" "}
                  &middot; {journey.enrollment_count} enrollment
                  {journey.enrollment_count !== 1 ? "s" : ""}
                </div>
              </div>
              <span
                style={{
                  padding: "4px 12px",
                  borderRadius: "12px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#fff",
                  backgroundColor: statusColors[journey.status] || "#6b7280",
                }}
              >
                {journey.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowCreateModal(false)}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "24px",
              borderRadius: "8px",
              width: "400px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: "0 0 16px 0" }}>Create Journey</h2>
            <input
              type="text"
              placeholder="Journey name"
              value={newJourneyName}
              onChange={(e) => setNewJourneyName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
              autoFocus
            />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                marginTop: "16px",
              }}
            >
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !newJourneyName.trim()}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#000",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: creating ? "not-allowed" : "pointer",
                  opacity: creating || !newJourneyName.trim() ? 0.5 : 1,
                }}
              >
                {creating ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Journeys;
